import { Choice } from "../simulation-decisions/types"

export enum ParticipantRole {
  MEMBER = "member",
  LEADER = "leader"
}

export enum ParticipantStatus {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  AWAY = "away"
}

export enum VotingStatus {
  PENDING = "pending",
  APPROVED = "approved",
  REJECTED = "rejected",
  EXPIRED = "expired"
}

export enum VotingThreshold {
  SIMPLE_MAJORITY = "simple_majority", // >50%
  SUPERMAJORITY = "supermajority",     // >=2/3
  UNANIMOUS = "unanimous"              // 100%
}

export enum RoomStatus {
  WAITING = "waiting",     // Waiting for participants to join
  READY = "ready",         // All participants joined, ready to start
  ACTIVE = "active",       // Simulation running
  COMPLETED = "completed", // Simulation finished
  CANCELLED = "cancelled"  // Session cancelled
}

// New: Action permission types for Roots & Shoots context
export enum ActionPermissionType {
  INDIVIDUAL = "individual",      // Can be done alone (info sharing, communication)
  REQUIRES_VOTING = "requires_voting", // Requires team voting (major decisions)
  CONTEXT_DEPENDENT = "context_dependent" // Permission depends on specific action context
}

// New: Context-specific permissions for Roots & Shoots scenarios
export type ActionPermission = {
  actionType: string
  permissionType: ActionPermissionType
  requiredThreshold?: VotingThreshold
  description: string
  rationale: string
}

// New: Scenario context for Roots & Shoots
export type ScenarioContext = {
  id: string
  name: string
  description: string
  organizationName: string
  userRole: string
  backgroundInfo: string
  keyStakeholders: string[]
  currentSituation: string
  actionPermissions: ActionPermission[]
}

export type Participant = {
  id: string
  userId: number
  firstName: string
  lastName: string
  email: string
  role: ParticipantRole
  status: ParticipantStatus
  joinedAt: string
  lastActive: string
}

export type Vote = {
  participantId: string
  decision: 'approve' | 'reject' | 'abstain'
  timestamp: string
}

export type CollaborativeChoice = {
  id: string
  proposedBy: string
  proposedByName: string
  choice: Choice
  votes: Vote[]
  status: VotingStatus
  proposedAt: string
  votingDeadline: string
  requiredThreshold: VotingThreshold
}

export type SimulationRoom = {
  id: string
  testId: number
  organizationId: number
  name: string
  description?: string
  maxParticipants: number
  currentParticipants: number
  status: RoomStatus
  createdBy: number
  createdAt: string
  scheduledStart?: string
  startedAt?: string
  completedAt?: string
  votingThreshold: VotingThreshold
  votingTimeLimit: number // seconds
}

export type RoomParticipant = {
  roomId: string
  participantId: string
  participant: Participant
  joinedAt: string
}

export type CollaborationState = {
  currentRoom: SimulationRoom | null
  participants: Participant[]
  pendingChoices: CollaborativeChoice[]
  completedChoices: CollaborativeChoice[]
  chatMessages: ChatMessage[]
  privateConversations: PrivateConversation[]
  activeConversationId: string | null // null = public chat, string = private conversation ID
  isConnected: boolean
  currentUserId: number | null
  votingModalOpen: boolean
  currentVotingChoice: CollaborativeChoice | null
  transcription: TranscriptionState
  // Admin-only transcript storage for backend access
  sessionTranscripts: SessionTranscript[]
  currentSessionTranscript: SessionTranscript | null
}

// Transcription types
export type TranscriptionWord = {
  text: string
  start: number
  end: number
  confidence: number
  speaker?: string
}

export type TranscriptionSegment = {
  id: string
  text: string
  start: number
  end: number
  confidence: number
  speaker?: string
  words: TranscriptionWord[]
  isFinal: boolean
  participantId?: string
  timestamp: string
}

export type TranscriptionState = {
  segments: TranscriptionSegment[]
  partialSegments: TranscriptionSegment[]
  isTranscriptionEnabled: boolean
  isTranscriptionActive: boolean
  transcriptionErrors: string[]
}

// WebSocket message types for real-time communication
export enum WebSocketMessageType {
  PARTICIPANT_JOINED = "participant_joined",
  PARTICIPANT_LEFT = "participant_left",
  PARTICIPANT_STATUS_CHANGED = "participant_status_changed",
  CHOICE_PROPOSED = "choice_proposed",
  VOTE_CAST = "vote_cast",
  CHOICE_RESOLVED = "choice_resolved",
  SIMULATION_STATE_UPDATE = "simulation_state_update",
  TIMER_UPDATE = "timer_update",
  VIDEO_SIGNAL = "video_signal",
  VIDEO_CALL_START = "video_call_start",
  VIDEO_CALL_END = "video_call_end",
  AUDIO_TOGGLE = "audio_toggle",
  VIDEO_TOGGLE = "video_toggle",
  CHAT_MESSAGE = "chat_message",
  PRIVATE_MESSAGE = "private_message",
  CONVERSATION_CREATED = "conversation_created",
  TRANSCRIPTION_SEGMENT = "transcription_segment",
  TRANSCRIPTION_ERROR = "transcription_error",
  ERROR = "error"
}

export type WebSocketMessage = {
  type: WebSocketMessageType
  payload: Record<string, unknown>
  timestamp: string
  fromParticipant?: string
}

// API Request/Response types
export type CreateRoomRequest = {
  testId: number
  organizationId: number
  teamSimulationId?: string
  name: string
  description?: string
  maxParticipants: number
  votingThreshold: VotingThreshold
  votingTimeLimit: number
  scheduledStart?: string
}

export type CreateRoomResponse = {
  status: string
  code: number
  message: string
  data: SimulationRoom
}

export type JoinRoomRequest = {
  roomId: string
  userId: number
  firstName?: string
  lastName?: string
  role?: string
}

export type JoinRoomResponse = {
  status: string
  code: number
  message: string
  data: {
    room: SimulationRoom
    participant: Participant // API returns the newly joined participant (singular)
  }
}

export type ProposeChoiceRequest = {
  roomId: string
  proposedBy: string
  choice: Choice
}

export type ProposeChoiceResponse = {
  status: string
  code: number
  message: string
  data: CollaborativeChoice
}

export type CastVoteRequest = {
  roomId: string
  choiceId: string
  participantId: string
  decision: 'approve' | 'reject' | 'abstain'
}

export type CastVoteResponse = {
  status: string
  code: number
  message: string
  data: {
    choice: CollaborativeChoice
    resolved: boolean
  }
}

// Chat message types
export enum MessageType {
  PUBLIC = "public",
  PRIVATE = "private"
}

export type ChatMessage = {
  id: string
  userId: number
  userName: string
  message: string
  timestamp: string
  roomId: string
  messageType: MessageType
  recipientIds?: number[] // For private messages - who can see this message
  conversationId?: string // Groups private messages into conversations
}

export type PrivateConversation = {
  id: string
  participantIds: number[]
  participantNames: string[]
  lastMessage?: ChatMessage
  lastMessageAt: string
  createdAt: string
  roomId: string
}

export type ConversationPreview = {
  conversation: PrivateConversation
  unreadCount: number
  isActive: boolean
}

// Admin transcript types for backend access
export type SessionTranscript = {
  sessionId: string
  roomId: string
  testId: number
  organizationId: number
  startTime: string
  endTime: string
  participants: string[]
  entries: TranscriptEntry[]
  summary: TranscriptSummary
  createdAt: string
}

export type TranscriptEntry = {
  id: string
  timestamp: string
  type: 'chat' | 'transcription'
  participantId: string
  participantName: string
  content: string
  confidence?: number
  isFinal?: boolean
}

export type TranscriptSummary = {
  totalDuration: number
  chatMessageCount: number
  transcriptionSegmentCount: number
  participantSpeakingTime: Record<string, number>
}